import { useMemo } from "react"
import type { ChangeEvent, FormEvent } from "react"
import { Link, useSearchParams } from "react-router-dom"
import EntryFormField, {
  FIELD_CONTROL_CLASS,
} from "@/components/entry/EntryFormField"
import EntryFormSectionHeading from "@/components/entry/EntryFormSectionHeading"
import EntryGymFields from "@/components/entry/EntryGymFields"
import EntryPlayerFields from "@/components/entry/EntryPlayerFields"
import EmptyMessage from "@/components/common/EmptyMessage"
import Loading from "@/components/common/Loading"
import { useEntryModify } from "@/domain/entry/hooks/EntryModifyHook"
import { sportLabel } from "@/domain/fight/model/Fight"
import { fightSportPayloadToJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import type { SelectOption } from "@/domain/entry/model/Entry"

/**
 * 참가 신청 수정 화면.
 *
 * 신청번호는 조회 화면에서 넘어오고, 본인 확인(연락처 + 비밀번호)을 다시 받아 폼을 채운다.
 * 저장은 **선수 목록 전체 교체**이므로 지운 선수는 그대로 사라진다.
 */
export default function EntryModifyPage() {
  const [searchParams] = useSearchParams()
  const entryNo = searchParams.get("entryNo") ?? ""

  const {
    gymContactPhoneNo,
    password,
    detail,
    tournament,
    gym,
    players,
    gymErrors,
    playerErrors,
    errorMessage,
    isLoading,
    isModified,
    setGymContactPhoneNo,
    setPassword,
    setGymField,
    setPlayerField,
    setPlayerWeightClass,
    getWeightClassChoices,
    addPlayer,
    removePlayer,
    load,
    submit,
  } = useEntryModify(entryNo)

  /** 종목 선택지는 대회가 개최하는 종목만 노출한다 */
  const sportOptions = useMemo<SelectOption[]>(
    () =>
      (tournament?.sports ?? []).map((sport) => ({
        value: fightSportPayloadToJSON(sport),
        label: sportLabel(sport),
      })),
    [tournament],
  )

  function onChangeGymContactPhoneNo(event: ChangeEvent<HTMLInputElement>) {
    setGymContactPhoneNo(event.target.value)
  }

  function onChangePassword(event: ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value)
  }

  function onSubmitVerify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void load()
  }

  function onSubmitModify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void submit()
  }

  function onClickAddPlayer() {
    addPlayer()
  }

  if (!entryNo) {
    return (
      <section className="app-page max-w-[672px]">
        <EmptyMessage message="수정할 신청을 조회 화면에서 선택해 주세요." />
        <Link
          to="/application/lookup"
          className="focus-ring app-btn app-btn-primary mx-auto flex max-w-xs"
        >
          신청 조회로
        </Link>
      </section>
    )
  }

  return (
    <section className="app-page">
      <p className="app-txt-eyebrow text-app-primary-600">APPLICATION MODIFY</p>
      <h1 className="app-txt-h1 mt-3">참가 신청 수정</h1>
      <p className="app-txt-body2 mt-3 leading-6 text-app-text-300">
        신청번호 <span className="font-mono">{entryNo}</span> 의 내용을
        수정합니다. 선수 목록은 저장 시 화면에 남은 선수로 교체됩니다.
      </p>

      {!detail && (
        <form
          onSubmit={onSubmitVerify}
          noValidate
          className="app-card mt-8"
        >
          <EntryFormSectionHeading
            step="01"
            title="본인 확인"
            description="신청 시 입력한 체육관 연락처와 비밀번호를 입력해 주세요."
          />

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <EntryFormField
              controlId="modify-gym-contact-phone-no"
              label="체육관 연락처"
              isRequired
            >
              <input
                id="modify-gym-contact-phone-no"
                type="tel"
                inputMode="tel"
                value={gymContactPhoneNo}
                maxLength={13}
                onChange={onChangeGymContactPhoneNo}
                placeholder="010-1234-5678"
                className={FIELD_CONTROL_CLASS}
              />
            </EntryFormField>

            <EntryFormField
              controlId="modify-password"
              label="신청 비밀번호"
              isRequired
            >
              <input
                id="modify-password"
                type="password"
                autoComplete="current-password"
                value={password}
                maxLength={30}
                onChange={onChangePassword}
                className={FIELD_CONTROL_CLASS}
              />
            </EntryFormField>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="focus-ring app-btn app-btn-primary app-btn-block mt-6"
          >
            신청 내용 불러오기
          </button>
        </form>
      )}

      {isLoading && <Loading label="처리 중" />}

      {errorMessage && (
        <p
          role="alert"
          className="app-alert app-alert-error mt-5"
        >
          {errorMessage}
        </p>
      )}

      {isModified && (
        <div className="app-alert app-alert-success mt-5">
          <p
            role="status"
            className="app-txt-body2 font-bold"
          >
            신청 내용이 수정되었습니다.
          </p>
          <Link
            to="/application/lookup"
            className="focus-ring app-txt-button mt-2 inline-block hover:underline"
          >
            신청 조회로 돌아가기 →
          </Link>
        </div>
      )}

      {detail && !isModified && (
        <form
          onSubmit={onSubmitModify}
          noValidate
          className="mt-8 flex flex-col gap-5"
        >
          <EntryGymFields
            gym={gym}
            errors={gymErrors}
            onChange={setGymField}
          />

          <section className="app-card">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
              <EntryFormSectionHeading
                step="02"
                title="선수 수정"
                description="선수를 지우면 저장 시 신청에서 빠집니다."
              />
              <button
                type="button"
                onClick={onClickAddPlayer}
                className="focus-ring app-btn app-btn-brand shrink-0"
              >
                + 선수 추가
              </button>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {players.map((player, index) => (
                <EntryPlayerFields
                  key={player.key}
                  player={player}
                  index={index}
                  errors={playerErrors[player.key] ?? {}}
                  sportOptions={sportOptions}
                  weightClassChoices={getWeightClassChoices(player.key)}
                  canDelete={players.length > 1}
                  onChange={(field, value) =>
                    setPlayerField(player.key, field, value)
                  }
                  onChangeWeightClass={(optionKey) =>
                    setPlayerWeightClass(player.key, optionKey)
                  }
                  onDelete={() => removePlayer(player.key)}
                />
              ))}
            </div>
          </section>

          <button
            type="submit"
            disabled={isLoading}
            className="focus-ring app-btn app-btn-lg app-btn-primary app-btn-block"
          >
            수정 내용 저장
          </button>
        </form>
      )}
    </section>
  )
}
